import instance from "../../../config/axios";
import url from "../../../constants/url";
import { getcreateAuditlog } from "../../../apis";
import { decryptInfo, encryptInfo } from "../../../utils/crypto";
import { USER_SEARCH_LIMIT } from "../../../constants/powerBi";
import type {
  AuditLogEntry,
  PowerBiReport,
  PowerBiReportsListResponse,
  PowerBiListPayload,
  PowerBiReportPayload,
  PowerBiReportUpdatePayload,
  PowerBiCreateReportResponse,
  PowerBiMessageResponse,
  PowerBiRolesResponse,
  PowerBiRoleReportsResponse,
  PowerBiReportRolesResponse,
  PowerBiUserSearchParams,
  PowerBiUsersSearchResponse,
  PowerBiUserReportsResponse,
} from "./types";

const base = `${url.packagingapiurl}power-bi`;

// The backend reads raw paths (gin UseRawPath), so escape exactly like Go does or it gets e.g. "R%26D Manager".
export function encodePathSegment(value: string): string {
  return encodeURIComponent(value)
    .replace(/[!'()*]/g, (char) => `%${char.charCodeAt(0).toString(16).toUpperCase()}`)
    .replace(/%(24|26|2B|2C|3A|3B|3D|40)/g, (escaped) => decodeURIComponent(escaped));
}

// embed_url travels encrypted (utils/crypto, as login does); a plain http(s) URL from an older backend passes through.
export function decryptEmbedUrl(value?: string): string {
  if (!value || /^https?:\/\//.test(value)) return value || "";
  return decryptInfo(value);
}

function withPlainEmbedUrl<T extends { embed_url?: string }>(report: T): T {
  return report?.embed_url ? { ...report, embed_url: decryptEmbedUrl(report.embed_url) } : report;
}

function withEncryptedEmbedUrl<T extends { embed_url?: string }>(data: T): T {
  return data.embed_url ? { ...data, embed_url: encryptInfo(data.embed_url) } : data;
}

export const listPowerBiReports = (payload: PowerBiListPayload): Promise<PowerBiReportsListResponse> =>
  instance
    .post<PowerBiReportsListResponse, PowerBiReportsListResponse>(`${base}/reports/list`, payload)
    .then((res: PowerBiReportsListResponse) => ({ ...res, reports: (res?.reports || []).map(withPlainEmbedUrl) }));

export const getPowerBiReport = (reportId: string): Promise<PowerBiReport> =>
  instance
    .get<PowerBiReport, PowerBiReport>(`${base}/reports/details/${encodePathSegment(reportId)}`)
    .then(withPlainEmbedUrl);

export const createPowerBiReport = (data: PowerBiReportPayload): Promise<PowerBiCreateReportResponse> =>
  instance.post<PowerBiCreateReportResponse, PowerBiCreateReportResponse>(`${base}/reports/create`, withEncryptedEmbedUrl(data));

export const updatePowerBiReport = (reportId: string, data: PowerBiReportUpdatePayload): Promise<PowerBiMessageResponse> =>
  instance.put<PowerBiMessageResponse, PowerBiMessageResponse>(
    `${base}/reports/update/${encodePathSegment(reportId)}`,
    withEncryptedEmbedUrl(data)
  );

export const togglePowerBiReport = (reportId: string, isActive: boolean, requestedBy: string): Promise<PowerBiMessageResponse> =>
  instance.patch<PowerBiMessageResponse, PowerBiMessageResponse>(`${base}/reports/toggle/${encodePathSegment(reportId)}`, undefined, {
    params: { active: isActive, requested_by: requestedBy },
  });

export const getRolePowerBiReports = (roleName: string): Promise<PowerBiRoleReportsResponse> =>
  instance
    .get<PowerBiRoleReportsResponse, PowerBiRoleReportsResponse>(`${base}/roles/${encodePathSegment(roleName)}/reports`)
    .then((res: PowerBiRoleReportsResponse) => ({ ...res, reports: (res?.reports || []).map(withPlainEmbedUrl) }));

// user_ids is omitted, so every active user of the role gets the reports.
export const setRolePowerBiReports = (roleName: string, reportIds: string[], requestedBy: string): Promise<PowerBiMessageResponse> =>
  instance.put<PowerBiMessageResponse, PowerBiMessageResponse>(`${base}/roles/${encodePathSegment(roleName)}/reports`, {
    report_ids: reportIds,
    requested_by: requestedBy,
  });

export const getReportPowerBiRoles = (reportId: string): Promise<PowerBiReportRolesResponse> =>
  instance.get<PowerBiReportRolesResponse, PowerBiReportRolesResponse>(
    `${base}/reports/${encodePathSegment(reportId)}/roles`
  );

// userIds null → backend grants every active user of roleNames; otherwise exactly these users.
export const setReportPowerBiRoles = (
  reportId: string,
  roleNames: string[],
  userIds: string[] | null,
  requestedBy = ""
): Promise<PowerBiMessageResponse> =>
  instance.put<PowerBiMessageResponse, PowerBiMessageResponse>(`${base}/reports/${encodePathSegment(reportId)}/roles`, {
    role_names: roleNames,
    ...(userIds ? { user_ids: userIds } : {}),
    requested_by: requestedBy,
  });

export const getPowerBiRoles = (company = ""): Promise<PowerBiRolesResponse> =>
  instance.get<PowerBiRolesResponse, PowerBiRolesResponse>(`${base}/all-roles`, {
    params: company ? { company } : {},
  });

export const searchPowerBiUsers = ({
  email = "",
  role = "",
  company = "",
  limit = USER_SEARCH_LIMIT,
  offset = 0,
}: PowerBiUserSearchParams = {}): Promise<PowerBiUsersSearchResponse> => {
  const emailText = email.trim();
  return instance.get<PowerBiUsersSearchResponse, PowerBiUsersSearchResponse>(`${base}/users/search`, {
    params: {
      ...(emailText ? { email: emailText } : {}),
      ...(role ? { role } : {}),
      ...(company ? { company } : {}),
      limit,
      offset,
    },
  });
};

export const getUserPowerBiReports = (userId: string): Promise<PowerBiUserReportsResponse> =>
  instance.get<PowerBiUserReportsResponse, PowerBiUserReportsResponse>(
    `${base}/users/${encodePathSegment(userId)}/reports`
  );

// The user's access becomes exactly reportIds; [] removes all their Power BI access.
export const setUserPowerBiReports = (userId: string, reportIds: string[], requestedBy: string): Promise<PowerBiMessageResponse> =>
  instance.put<PowerBiMessageResponse, PowerBiMessageResponse>(`${base}/users/${encodePathSegment(userId)}/reports`, {
    report_ids: reportIds,
    requested_by: requestedBy,
  });

// Writes to the shared audit log (same endpoint the other modules use). No-op for an empty list.
export const createPowerBiAuditLogs = (entries: AuditLogEntry[]): Promise<void> =>
  entries.length > 0 ? getcreateAuditlog(entries).then(() => undefined) : Promise.resolve();
